import { atom } from "jotai"
import { currentAdjustmentsAtom, saveCurrentAdjustmentState } from "@/store/store-history"
import {
	createMaskGroupsForMasks,
	createMaskGroup,
	mapMasks,
	MappedMask,
	MappedMaskGroup,
	transformMask,
} from "@/store/editor/mask-utils"
import { Mask, MaskDetectionState, MaskGroup, MaskType } from "@/core/types"
import { getEditor, useEditor } from "@/utils/useEditor"
import { modify, remove } from "optics-ts"
import { store } from "@/store/store"
import { activeEditingToolPanelAtom } from "@/store/ui-views-state"
import toast from "react-hot-toast"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer toast i18n
import i18n from "@/i18n"
import { capitalized } from "@/utils/string"
import { MainAIMaskResolver } from "@/io/assets/ai-mask/main-ai-mask-resolver"
import { MaskAdjustmentFocusKey } from "../../core/adjustments"
import { currentPhotoIdAtom } from "../photo/photos-state"
import { processingQueue } from "../processing/processing-queue"
import { Photo } from "@/core/photo"
import { PhotoId } from "../undoable-state"
import {
	failMaskGroupThumbnailReservation,
	releaseMaskGroupThumbnailReservation,
	removeMaskGroupThumbnail,
	setMaskGroupThumbnailBlob,
} from "@/store/editor/mask-thumbnails"
import {
	isRetouchingEnabled,
	isRetouchingMasksDebugEnabled,
} from "@/utils/measurement/feature-flag"
import { MainMaskCache } from "@/storage/mask-cache"
import { projectAtom } from "@/store/project/projects-state"

const selectedMaskGroupIdAtom = atom<null | string>(null)
const selectedMaskIdAtom = atom<null | string>(null)

export const maskDetectionStateAtom = atom<Record<string, MaskDetectionState>>({})

export const maskDetectionCoordsVersionAtom = atom(0)

export const showingFloatingMaskingPanelAtom = atom<boolean>(false)

export const showingMaskingToolsOverlayAtom = atom<boolean>(false)

export const maskOverlaySettingsAtom = atom({ showOverlay: false, previewAdjustedMask: false })

export const showingMaskingMenuAtom = atom<boolean>(false)

export const focusedMaskAdjustmentAtom = atom<null | MaskAdjustmentFocusKey>(null)

const maskThumbnailRefreshTokens = new Map<string, symbol>()

export const mappedMasksAtom = atom((get) => {
	const adjustments = get(currentAdjustmentsAtom)
	const mappedMasks = mapMasks(adjustments.localAdjustments ?? [])

	return isRetouchingMasksDebugEnabled()
		? mappedMasks
		: mappedMasks.filter((group) => !group.properties.retouching)
})

export const pendingMaskGroupAtom = atom<MappedMaskGroup[]>([])

export const selectedMaskGroupAtom = atom(
	(get) => {
		const activeTool = get(activeEditingToolPanelAtom)
		const mapped = get(mappedMasksAtom)
		const id = get(selectedMaskGroupIdAtom)

		if (!id || activeTool !== "masking") {
			return
		}

		return mapped.find((group) => group.properties.id === id)
	},
	(get, set, group?: MaskGroup | string | null) => {
		const currentGroupId = get(selectedMaskGroupIdAtom)
		const selectedGroupId = (typeof group === "string" ? group : group?.id) ?? null
		set(selectedMaskGroupIdAtom, selectedGroupId)

		if (selectedGroupId !== null && selectedGroupId !== currentGroupId) {
			set(maskOverlaySettingsAtom, (settings) => ({ ...settings, showOverlay: true }))
		}

		if (group && typeof group !== "string") {
			// Also select the last mask in the group
			const id = group.masks.at(-1)?.id
			set(selectedMaskIdAtom, id ?? null)
		}
	},
)

export const selectedMaskAtom = atom(
	(get) => {
		const group = get(selectedMaskGroupAtom)
		const id = get(selectedMaskIdAtom)

		if (!id || !group) {
			return
		}

		return group.children.find((mask) => mask.properties.id === id)
	},
	(_, set, mask?: Mask | string | null) => {
		if (mask) {
			const id = typeof mask === "string" ? mask : mask.id
			set(selectedMaskIdAtom, id ?? null)
			set(showingMaskingToolsOverlayAtom, true)
		} else {
			set(selectedMaskIdAtom, null)
		}
	},
)

/**
 * Mask group modifiers (optics)
 */

// Check if the photo has changed. e.g. user has switched to another photo
export const checkPhotoId = (processingPhotoId: PhotoId | null) => {
	return store.get(currentPhotoIdAtom) === processingPhotoId
}

export const AddMaskGroupWithMask = async (mask: Mask) => {
	return AddMaskGroupWithMasks([mask])
}

export const AddMaskGroupWithMasks = async (
	masks: Mask[],
	options: { separateGroups?: boolean } = {},
) => {
	store.set(showingMaskingMenuAtom, false)

	const editor = useEditor()

	if (masks.length === 0) {
		return
	}

	const processingPhotoId = store.get(currentPhotoIdAtom)

	for (const mask of masks) {
		const detectedResult = MainAIMaskResolver.hasMaskDetected(editor.photo, mask)
		if (detectedResult === false) {
			showNoDetected(mask.type)
			return
		}
	}

	masks.forEach((mask, index) => {
		if (!mask.name) {
			mask.name = `${mask.type} ${index + 1}`
		} else if (masks.length === 1 && mask.type !== "people") {
			mask.name = `${mask.name} 1`
		}
	})

	const groups = [...store.get(mappedMasksAtom), ...store.get(pendingMaskGroupAtom)]

	const maskGroups = createMaskGroupsForMasks({
		masks,
		startIndex: groups.length,
		separateGroups: options.separateGroups,
	})
	const getMaskGroupsForCommit = () => {
		const startIndex = getEditor().adjustments.localAdjustments?.length ?? 0
		return maskGroups.map((group, index) => ({
			...group,
			name: `Mask ${startIndex + index + 1}`,
		}))
	}

	const detectableMaskGroup = createMaskGroup({
		name: maskGroups[0]?.name ?? `Mask ${groups.length + 1}`,
		masks,
	})

	const masksToDetect = masks.filter(
		(mask) => MainAIMaskResolver.hasMaskDetected(editor.photo, mask) !== true,
	)

	if (masksToDetect.length === 0) {
		if (checkPhotoId(processingPhotoId)) {
			await addMaskGroups(getMaskGroupsForCommit())
		}
	} else {
		const mappedGroups = mapMasks(maskGroups.length > 0 ? maskGroups : [detectableMaskGroup])
		store.set(pendingMaskGroupAtom, (prevState) => [...mappedGroups, ...prevState])

		let detectionResult = true
		for (const mask of masksToDetect) {
			const result = await detectMaskSequentially(editor.photo, mask)
			if (!result) {
				detectionResult = false
				break
			}
		}

		store.set(pendingMaskGroupAtom, (prevState) =>
			prevState.filter((_group) => {
				return !mappedGroups.some((group) => group.properties.id === _group.properties.id)
			}),
		)

		if (detectionResult) {
			if (checkPhotoId(processingPhotoId)) {
				await addMaskGroups(getMaskGroupsForCommit())
			}
		} else {
			const failedMaskType = masksToDetect[0]?.type ?? masks[0]?.type
			if (failedMaskType) {
				showNoDetected(failedMaskType)
			}
		}
	}
}

async function createMaskGroupThumbnailBestEffort(photo: Photo, masks: Mask[]) {
	try {
		return await MainAIMaskResolver.createMaskGroupThumbnail(photo, masks)
	} catch {
		return undefined
	}
}

export async function refreshMaskGroupThumbnail(
	groupId: string,
	transformKey: string,
	maskGroupDigest: string,
) {
	if (!isRetouchingEnabled()) {
		return
	}

	const editor = getEditor()
	const processingPhotoId = store.get(currentPhotoIdAtom)
	const processingProjectId = store.get(projectAtom)?.id
	if (!processingPhotoId || editor.photo.id !== processingPhotoId) {
		return
	}

	const groupMasks = editor.adjustments.localAdjustments?.find(
		(group) => group.id === groupId,
	)?.masks

	if (!groupMasks) {
		return
	}

	const refreshKey = `${processingPhotoId}:${groupId}`
	const refreshToken = Symbol()
	maskThumbnailRefreshTokens.set(refreshKey, refreshToken)

	try {
		const thumbnail = await createMaskGroupThumbnailBestEffort(editor.photo, groupMasks)

		if (
			!checkPhotoId(processingPhotoId) ||
			store.get(projectAtom)?.id !== processingProjectId ||
			maskThumbnailRefreshTokens.get(refreshKey) !== refreshToken ||
			!getEditor().adjustments.localAdjustments?.some((group) => group.id === groupId)
		) {
			return
		}

		if (thumbnail === undefined) {
			failMaskGroupThumbnailReservation(
				processingPhotoId,
				groupId,
				transformKey,
				maskGroupDigest,
			)
			return
		}

		if (
			!setMaskGroupThumbnailBlob(
				processingPhotoId,
				groupId,
				transformKey,
				maskGroupDigest,
				thumbnail,
			)
		) {
			failMaskGroupThumbnailReservation(
				processingPhotoId,
				groupId,
				transformKey,
				maskGroupDigest,
			)
			return
		}
		void MainMaskCache.writeMaskGroupThumbnail({
			photoId: processingPhotoId,
			groupId,
			transformKey,
			maskGroupDigest,
			data: thumbnail,
		})
	} finally {
		releaseMaskGroupThumbnailReservation(
			processingPhotoId,
			groupId,
			transformKey,
			maskGroupDigest,
		)
		if (maskThumbnailRefreshTokens.get(refreshKey) === refreshToken) {
			maskThumbnailRefreshTokens.delete(refreshKey)
		}
	}
}

export async function detectMaskSequentially(photo: Photo, mask: Mask) {
	return new Promise((resolve) => {
		processingQueue.addTask(photo.id, {
			purpose: "calculate",
			handler: async () => {
				// AI detection happens in a serial queue
				const result = await MainAIMaskResolver.detectMask(photo, mask)
				resolve(result)
			},
			taskRecord: { type: "detect-mask", maskId: mask.id, maskType: mask.type },
		})
	})
}

export function showNoDetected(type: MaskType) {
	switch (type) {
		case "sky":
			toast.error(i18n.t("store:masksState.noSkyDetectedError"))
			break
		case "background":
			toast.error(i18n.t("store:masksState.noBackgroundDetectedError"))
			break
		case "subject":
			toast.error(i18n.t("store:masksState.noSubjectDetectedError"))
			break
		case "people":
			toast.error(i18n.t("store:masksState.noPeopleDetectedError"))
			break
	}
}

export const updateMaskGroup = (group: MappedMaskGroup, properties: Partial<MaskGroup>) => {
	const editor = getEditor()

	const localAdjustments = modify(group.lens)((adjustments) => {
		return {
			...adjustments,
			...properties,
		}
	})(editor.adjustments.localAdjustments ?? [])

	editor.setAdjustments({ localAdjustments })
}

export const removeMaskGroup = (group: MappedMaskGroup) => {
	const editor = getEditor()
	const photoId = store.get(currentPhotoIdAtom)
	const localAdjustments = remove(group.lens)(editor.adjustments.localAdjustments ?? [])

	editor.setAdjustments({ localAdjustments })
	for (const mask of group.properties.masks) {
		if (mask.type === "people") {
			MainAIMaskResolver.releaseCommittedMask(mask.id)
		}
	}

	saveCurrentAdjustmentState({
		message: `Remove Mask`,
		changedValue: group.properties.name,
	})
	if (photoId) {
		maskThumbnailRefreshTokens.delete(`${photoId}:${group.properties.id}`)
		removeMaskGroupThumbnail(photoId, group.properties.id)
		void MainMaskCache.deleteMaskGroupThumbnail(photoId, group.properties.id)
	}

	// After removing, select the most recent mask group
	store.set(selectedMaskGroupAtom, localAdjustments.at(-1))
}

export const addMaskGroup = async (maskGroup: MaskGroup) => {
	await addMaskGroups([maskGroup])
}

export const addMaskGroups = async (maskGroups: MaskGroup[]) => {
	const editor = getEditor()
	const processingPhotoId = store.get(currentPhotoIdAtom)
	const localAdjustments = editor.adjustments.localAdjustments ?? []

	const transformedMaskGroups = maskGroups.map((maskGroup) => ({
		...maskGroup,
		masks: maskGroup.masks.map((mask) =>
			transformMask(
				mask,
				editor.mainViewport.cropViewMatrixInv,
				editor.crop,
				editor.rotation,
			),
		),
	}))

	editor.setAdjustments({ localAdjustments: [...localAdjustments, ...transformedMaskGroups] })

	const firstMaskGroup = transformedMaskGroups[0]
	if (firstMaskGroup?.masks[0] && firstMaskGroup.masks[0].type) {
		await saveCurrentAdjustmentState({
			message: `Add ${capitalized(firstMaskGroup.masks[0].type)} Mask`,
			changedValue:
				transformedMaskGroups.length === 1
					? firstMaskGroup.name
					: `${transformedMaskGroups.length} masks`,
		})
	} else {
		await saveCurrentAdjustmentState({
			message: `Add Mask`,
			changedValue:
				transformedMaskGroups.length === 1
					? firstMaskGroup?.name
					: `${transformedMaskGroups.length} masks`,
		})
	}

	if (checkPhotoId(processingPhotoId)) {
		store.set(selectedMaskGroupAtom, transformedMaskGroups.at(-1))
	}
}

/**
 * Individual mask modifiers (optics)
 */

export const updateMask = <T = Mask>(mask: MappedMask<T>, properties: Partial<T>) => {
	const editor = getEditor()

	const localAdjustments = modify(mask.lens)((mask) => {
		return {
			...mask,
			...properties,
		}
	})(editor.adjustments.localAdjustments ?? [])

	editor.setAdjustments({ localAdjustments })
}

export const addMasks = async (groupId: string, masks: Mask[]) => {
	if (masks.length === 0) {
		return
	}

	const editor = getEditor()
	const processingPhotoId = store.get(currentPhotoIdAtom)
	const currentGroups = editor.adjustments.localAdjustments ?? []
	const group = currentGroups.find((candidate) => candidate.id === groupId)
	if (!group || !checkPhotoId(processingPhotoId)) {
		return
	}

	const maskNames = new Set(group.masks.map((mask) => mask.name))
	const maskTypeCounts = new Map<Mask["type"], number>()
	for (const mask of group.masks) {
		maskTypeCounts.set(mask.type, (maskTypeCounts.get(mask.type) ?? 0) + 1)
	}
	const transformedMasks = masks.map((mask) => {
		const requestedName = mask.name || capitalized(mask.type)
		const typeCount = maskTypeCounts.get(mask.type) ?? 0
		const hasStableNumberedName =
			mask.type === "people" ? requestedName !== "People" : / \d+$/.test(requestedName)
		let name = requestedName
		if (!hasStableNumberedName || maskNames.has(name)) {
			const baseName =
				mask.type === "people" ? requestedName : requestedName.replace(/ \d+$/, "")
			let index = typeCount + 1
			do {
				name = `${baseName} ${index}`
				index += 1
			} while (maskNames.has(name))
		}
		maskNames.add(name)
		maskTypeCounts.set(mask.type, typeCount + 1)

		return transformMask(
			{ ...mask, name },
			editor.mainViewport.cropViewMatrixInv,
			editor.crop,
			editor.rotation,
		)
	})
	const localAdjustments = currentGroups.map((candidate) =>
		candidate.id === groupId
			? {
					...candidate,
					masks: [...candidate.masks, ...transformedMasks],
				}
			: candidate,
	)
	editor.setAdjustments({ localAdjustments })

	const firstMask = transformedMasks[0]
	await saveCurrentAdjustmentState({
		message: `Add ${firstMask?.type ?? ""} Mask`,
		changedValue:
			transformedMasks.length === 1 ? firstMask?.name : `${transformedMasks.length} masks`,
	})
	if (checkPhotoId(processingPhotoId)) {
		store.set(selectedMaskAtom, transformedMasks.at(-1))
	}
}

export const removeMask = async (mask: MappedMask) => {
	const editor = getEditor()
	const processingPhotoId = store.get(currentPhotoIdAtom)
	const selectedMask = store.get(selectedMaskAtom)
	const remainingMasks = mask.group.children.filter(
		(child) => child.properties.id !== mask.properties.id,
	)
	const nextSelectedMask =
		selectedMask?.properties.id === mask.properties.id
			? remainingMasks.at(-1)?.properties
			: selectedMask?.properties
	const localAdjustments = remove(mask.lens)(editor.adjustments.localAdjustments ?? [])

	editor.setAdjustments({ localAdjustments })
	if (mask.properties.type === "people") {
		MainAIMaskResolver.releaseCommittedMask(mask.properties.id)
	}

	await saveCurrentAdjustmentState({
		message: `Remove Mask`,
		changedValue: mask.properties.name,
	})

	if (checkPhotoId(processingPhotoId)) {
		store.set(selectedMaskAtom, nextSelectedMask)
	}
}
